import { createContext, ReactElement, useEffect, useState } from "react";
import { Box, Stepper, Typography, Step, StepLabel, Chip } from "@mui/material";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { styled } from "@mui/material/styles";
import { StepIconProps } from "@mui/material/StepIcon";
import DocumentUpload from "./DocumentUpload";
import DocumentsToDownload from "./DocumentsToDownload";
import { useProgress } from "context/ProgressProvider";
import { useSelector } from "react-redux";
import { DefaultPagination } from "services";
import { LoanService } from "services/LoanService";
import { DocumentStatusCode } from "shared/helpers/Constants";
import Utils from "shared/helpers/Utils";
import { AppPrimaryButton } from "shared/components/AppComponents";
import Toast from "shared/helpers/Toast";
import DocumentsToMail from "./DocumentsToMail";
// import DocumentUpload from "./components/DocumentUpload";

export const UploadDocumentContext = createContext({});

const ColorlibConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  // [`&.${stepConnectorClasses.active}`]: {
  //     [`& .${stepConnectorClasses.line}`]: {
  //         backgroundColor: '#2D68F7',
  //     },
  // },
  // [`&.${stepConnectorClasses.completed}`]: {
  //     [`& .${stepConnectorClasses.line}`]: {
  //         backgroundColor: '#2D68F7',
  //     },
  // },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 1,
    backgroundColor: "#E5E5E5",
    borderRadius: 1,
    margin: "30px 30px 0px 30px",
  },
}));

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ ownerState }) => ({
  backgroundColor:
    // ownerState.active || ownerState.completed ? "#2D68F7" : "#ccc",
    ownerState.active ? "#2D68F7" : "#ccc",
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "none",
  border: "none",
}));

const ColorlibStepIcon: React.FC<StepIconProps> = (props) => {
  const { active, completed, className, icon } = props;
  console.log("active, completed", active, completed);

  const icons: { [index: string]: React.ReactNode } = {
    1: (
      <img
        alt="Upload"
        src="/icons/icon_upload_blue.png"
        style={{
          filter: active ? "" : "invert(1)",
        }}
      />
    ),
    2: (
      <img
        alt="Download"
        src="/icons/icon_download.png"
        style={{
          filter: active
            ? "invert(100%) brightness(200%) contrast(150%)"
            : "invert(0%) brightness(100%) contrast(100%)",
        }}
      />
    ),
    3: (
      <img
        alt="Mail"
        src="/icons/icon_mail.png"
        style={{
          filter: active
            ? "invert(100%) brightness(200%) contrast(150%)"
            : "invert(0%) brightness(100%) contrast(100%)",
        }}
      />
    ),
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
      sx={{ cursor: "pointer" }}
    >
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
};

export interface DocumentsProps {
  _id: string;
  documentName: string;
  documentDescription: string;
  instructions: any;
  docType: number;
  isDefault: boolean;
  isAppliedPerProperty: boolean;
  isPayout: boolean;
  isRefinance: boolean;
  isInterestOnly: boolean;
  isAdminInitiated: boolean;
  isAdminUploadForSign: boolean;
}

export interface UploadedDocumentsProps {
  loanDocumentId: string;
  applicantName: string;
  applicantEmail: string;
  requestedBy: string;
  requestedTime: string;
  loanId: string;
  applicationId: number;
  submittedBy: string;
  documents: DocumentsProps;
  documentId: string;
  createdAt: string;
  blobFilePath: string;
  blobFilePathToDownload: string;
  uploadedTime: string;
  uploadedBy: string;
  documentStatus: number;
  uploadedByName: string;
  adminApproval: {
    actionedBy: string;
    reason: string;
    status: number;
  };
}

export const getDocumentStatusColorCode = (status: number): ReactElement => (
  <Box className="status-chip-container">
    {status === DocumentStatusCode.REQUESTED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Requested</Typography>
          </>
        }
        className="pending"
        variant="filled"
        size="small"
      />
    )}
    {status === DocumentStatusCode.NEEDCLARIFICATION && (
      <Chip
        label={
          <>
            <Typography variant="body1">Need clarification</Typography>
          </>
        }
        className="rejected"
        variant="filled"
        size="small"
      />
    )}
    {status === DocumentStatusCode.UPLOADED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Uploaded</Typography>
          </>
        }
        className="started"
        variant="filled"
        size="small"
      />
    )}
    {status === DocumentStatusCode.ACCEPTED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Approved</Typography>
          </>
        }
        className="verified"
        variant="filled"
        size="small"
      />
    )}
  </Box>
);

const UploadDocuments = ({ setStep }): ReactElement => {
  const steps = ["Upload", "Download", "Mail"];
  const { currentProfile, isBroker } = useProgress();
  const [activeStep, setActiveStep] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [statusDocuments, setStatusDocuments] = useState("pending");
  const [pagination, setPagination] = useState({
    ...DefaultPagination(20),
  });

  // API Calls
  const loadDocumentsList = (): void => {
    setLoadingHistory(true);
    LoanService.getAllMappedDocuments(
      pagination.offset,
      pagination.limit,
      currentProfile.loadId,
    )
      .then((response: any) => {
        const { documents, pagination } = response.data.data;

        const findDocumentsForDownload = documents.filter(
          (document: UploadedDocumentsProps) =>
            document.blobFilePathToDownload !== null,
        );
        if (findDocumentsForDownload.length > 0) {
          setCompletedSteps(1);
        }
        setPagination(pagination);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setLoadingHistory(false);
      });
  };

  // Hooks
  useEffect(() => {
    loadDocumentsList();
  }, []);

  useEffect(() => {
    //
  }, [statusDocuments]);

  console.log("statusDocuments", statusDocuments);

  // UI Elements
  return (
    <UploadDocumentContext.Provider value={{ setActiveStep }}>
      <Box className="app-confirm-dialog pb-5">
        <Typography variant="h2" className="f20">
          Prepare your documents
        </Typography>
        <Typography className="sub-title mt-2">
          {isBroker ? (
            <>
              Kindly go through step by step to upload, download, certify and
              mailing us the certified documents to complete your customer
              business loan application.
            </>
          ) : (
            <>
              Kindly go through step by step to upload, download, certify and
              mailing us the certified documents to complete your business loan
              application.
            </>
          )}
        </Typography>
      </Box>

      <Box className="pb-5">
        <Stepper
          alternativeLabel
          activeStep={activeStep}
          connector={<ColorlibConnector />}
        >
          {steps.map((label: string, index: number) => (
            <Step key={label} completed={index <= completedSteps}>
              <StepLabel
                StepIconComponent={ColorlibStepIcon}
                onClick={() => setActiveStep(index)}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {activeStep === 0 && <DocumentUpload />}

      {activeStep === 1 && <DocumentsToDownload />}

      {activeStep === 2 && <DocumentsToMail />}

      {false && (
        <Box mt={4}>
          <AppPrimaryButton
            title="Continue"
            isLoading={false}
            loadingText="Please wait.."
            onClick={() => {
              if (statusDocuments === "progress") {
                Toast.showSuccessMessage(
                  isBroker
                    ? "Thanks for sending the documents. We’ll review them soon and may reach out for any additional documents needed to process your customer loan application."
                    : "Thanks for sending the documents. We’ll review them soon and may reach out for any additional documents needed to process your loan application.",
                );
                return;
              }
              setStep((prev) => prev + 1);
            }}
            disabled={statusDocuments === "pending"}
          />
        </Box>
      )}
    </UploadDocumentContext.Provider>
  );
};

export default UploadDocuments;
